import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

const BLUE = "#135597";
const LIGHT_BLUE = "#EEF5FB";
const BORDER = "#D9E2EC";
const TEXT = "#243447";
const MUTED = "#66788A";
const GREEN = "#2E7D32";

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    paddingTop: 48,
    paddingBottom: 45,
    paddingHorizontal: 55,
    color: TEXT,
    fontFamily: "Helvetica",
  },

  // Header
  header: {
    backgroundColor: BLUE,
    borderRadius: 6,
    paddingVertical: 16,
    paddingHorizontal: 20,
    marginBottom: 24,
  },

  headerTitle: {
    color: "#FFFFFF",
    fontSize: 21,
    fontWeight: "bold",
    textAlign: "center",
  },

  headerSubtitle: {
    color: "#DCEBFA",
    fontSize: 9,
    textAlign: "center",
    marginTop: 5,
  },

  // Intro
  intro: {
    marginBottom: 18,
    lineHeight: 1.5,
    fontSize: 10.5,
  },

  // Event information box
  infoBox: {
    backgroundColor: LIGHT_BLUE,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 6,
    padding: 12,
    marginBottom: 20,
  },

  infoTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: BLUE,
    marginBottom: 5,
  },

  infoText: {
    fontSize: 9.5,
    lineHeight: 1.5,
  },

  // Person
  personSection: {
    marginBottom: 20,
  },

  label: {
    fontSize: 8.5,
    color: MUTED,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },

  personName: {
    fontSize: 17,
    fontWeight: "bold",
    color: TEXT,
  },

  // Workshop cards
  workshopsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
  },

  workshopGrid: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 20,
  },

  workshopCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 6,
    padding: 13,
  },

  workshopTime: {
    fontSize: 8.5,
    color: BLUE,
    fontWeight: "bold",
    marginBottom: 8,
  },

  workshopName: {
    fontSize: 12,
    fontWeight: "bold",
    lineHeight: 1.35,
    marginBottom: 10,
  },

  workshopLocationLabel: {
    fontSize: 8,
    color: MUTED,
    marginBottom: 2,
  },

  workshopLocation: {
    fontSize: 9.5,
  },

  // Confirmation
  confirmedBox: {
    borderWidth: 1,
    borderColor: "#B7D7BB",
    backgroundColor: "#F3FAF4",
    borderRadius: 6,
    padding: 11,
    marginTop: 4,
    marginBottom: 15,
  },

  confirmedTitle: {
    color: GREEN,
    fontWeight: "bold",
    fontSize: 10,
    marginBottom: 3,
  },

  confirmedText: {
    fontSize: 9,
    color: TEXT,
    lineHeight: 1.4,
  },

  // Table
  tableTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
  },

  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 4,
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },

  tableRowLast: {
    flexDirection: "row",
  },

  tableHeader: {
    backgroundColor: LIGHT_BLUE,
  },

  tableCell: {
    paddingVertical: 7,
    paddingHorizontal: 6,
    fontSize: 8.5,
    borderRightWidth: 1,
    borderRightColor: BORDER,
  },

  tableHeaderText: {
    fontWeight: "bold",
    color: BLUE,
  },

  colNumber: {
    width: "7%",
  },

  colFirstName: {
    width: "18%",
  },

  colLastName: {
    width: "18%",
  },

  colMorning: {
    width: "28.5%",
  },

  colEvening: {
    width: "28.5%",
    borderRightWidth: 0,
  },

  // Footer
  footer: {
    position: "absolute",
    bottom: 22,
    left: 55,
    right: 55,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: BORDER,
    fontSize: 8,
    color: MUTED,
    textAlign: "center",
  },
});

const BookingPDF = ({
  workshops = [],
  userInfo,
  plannedBookings = [],
  morningLocation,
  eveningLocation,
}) => {
  const workshopMap = workshops.reduce((map, workshop) => {
    map[workshop.documentId] = workshop.name;
    return map;
  }, {});

  const morningWorkshopTitle =
    workshopMap[userInfo?.morning_workshop] || "—";

  const eveningWorkshopTitle =
    workshopMap[userInfo?.evening_workshop] || "—";

  const bookings = plannedBookings.map((booking) => ({
    ...booking,
    morning_workshop_title:
      workshopMap[booking.morning_workshop] || "—",
    evening_workshop_title:
      workshopMap[booking.evening_workshop] || "—",
  }));

  const hasPlannedBookings = bookings.length > 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            Registreeringu kinnitus
          </Text>

          <Text style={styles.headerSubtitle}>
            „Karjääriredel viib kosmosesse“
          </Text>
        </View>

        <Text style={styles.intro}>
          Täname registreerimise eest! Allpool on Sinu registreeringu
          ülevaade. Palun hoia see dokument alles, et vajalik info oleks
          Sul sündmuse päeval käepärast.
        </Text>

        <View style={styles.infoBox}>
          <Text style={styles.infoTitle}>
            KARJÄÄRIPÄEV
          </Text>

          <Text style={styles.infoText}>
            „Karjääriredel viib kosmosesse“ toimub Tartu Tamme
            Gümnaasiumis, Nooruse 9, Tartus.
          </Text>

          <Text style={[styles.infoText, { marginTop: 5 }]}>
            Palume kohal olla hiljemalt kell 9.30, et jõuaksite
            registreeruda.
          </Text>
        </View>

        <View style={styles.personSection}>
          <Text style={styles.label}>
            Registreerija
          </Text>

          <Text style={styles.personName}>
            {userInfo?.first_name} {userInfo?.last_name}
          </Text>
        </View>

        {!hasPlannedBookings ? (
          <>
            <Text style={styles.workshopsTitle}>
              Sinu töötoad
            </Text>

            <View style={styles.workshopGrid}>
              <View style={styles.workshopCard}>
                <Text style={styles.workshopTime}>
                  HOMMIK • 10.00–11.30
                </Text>

                <Text style={styles.workshopName}>
                  {morningWorkshopTitle}
                </Text>

                <Text style={styles.workshopLocationLabel}>
                  TOIMUMISKOHT
                </Text>

                <Text style={styles.workshopLocation}>
                  {morningLocation || "—"}
                </Text>
              </View>

              <View style={styles.workshopCard}>
                <Text style={styles.workshopTime}>
                  PÄRASTLÕUNA • 13.00–14.30
                </Text>

                <Text style={styles.workshopName}>
                  {eveningWorkshopTitle}
                </Text>

                <Text style={styles.workshopLocationLabel}>
                  TOIMUMISKOHT
                </Text>

                <Text style={styles.workshopLocation}>
                  {eveningLocation || "—"}
                </Text>
              </View>
            </View>
          </>
        ) : (
          <View>
            <Text style={styles.tableTitle}>
              Registreeritud õpilased
            </Text>

            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text
                  style={[
                    styles.tableCell,
                    styles.colNumber,
                    styles.tableHeaderText,
                  ]}
                >
                  #
                </Text>

                <Text
                  style={[
                    styles.tableCell,
                    styles.colFirstName,
                    styles.tableHeaderText,
                  ]}
                >
                  Eesnimi
                </Text>

                <Text
                  style={[
                    styles.tableCell,
                    styles.colLastName,
                    styles.tableHeaderText,
                  ]}
                >
                  Perenimi
                </Text>

                <Text
                  style={[
                    styles.tableCell,
                    styles.colMorning,
                    styles.tableHeaderText,
                  ]}
                >
                  Hommikune töötuba
                </Text>

                <Text
                  style={[
                    styles.tableCell,
                    styles.colEvening,
                    styles.tableHeaderText,
                  ]}
                >
                  Õhtune töötuba
                </Text>
              </View>

              {bookings.map((child, index) => {
                const isLast = index === bookings.length - 1;

                return (
                  <View
                    key={child.uuid || index}
                    style={
                      isLast
                        ? styles.tableRowLast
                        : styles.tableRow
                    }
                    wrap={false}
                  >
                    <Text
                      style={[
                        styles.tableCell,
                        styles.colNumber,
                      ]}
                    >
                      {index + 1}
                    </Text>

                    <Text
                      style={[
                        styles.tableCell,
                        styles.colFirstName,
                      ]}
                    >
                      {child.first_name || "—"}
                    </Text>

                    <Text
                      style={[
                        styles.tableCell,
                        styles.colLastName,
                      ]}
                    >
                      {child.last_name || "—"}
                    </Text>

                    <Text
                      style={[
                        styles.tableCell,
                        styles.colMorning,
                      ]}
                    >
                      {child.morning_workshop_title}
                    </Text>

                    <Text
                      style={[
                        styles.tableCell,
                        styles.colEvening,
                      ]}
                    >
                      {child.evening_workshop_title}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        <Text style={styles.footer}>
          Küsimuste korral kirjutage: esero@ut.ee
        </Text>
      </Page>
    </Document>
  );
};

export default BookingPDF;